// Manifest-poll cadence (docs/04 §Running, Sprint 11 traffic diet).
//
// Pure helpers so the cadence rules unit-test without the state machine.
//
// When the live WebSocket is CONNECTED the hub pushes `refresh_manifest`, so the
// player can poll far less often — it stretches the interval to at least
// WS_CONNECTED_POLL_S. A server-sent `poll_interval_s` still wins WHEN LARGER: the
// operator can ask for an even lazier cadence, but never a busier one than the
// stretch while WS carries the urgent nudges. On WS disconnect the caller drops
// back to the base interval and polls once (see app.ts).
//
// Precedence (longest wins while connected): explicit manifest poll_interval_s ≥
// WS-stretched default (300s) ≥ DEFAULT_POLL_INTERVAL_S (60s).

export const DEFAULT_POLL_INTERVAL_S = 60;
export const WS_CONNECTED_POLL_S = 300;

/** ±10% poll jitter so a fleet never polls in lockstep (thundering herd). */
export const POLL_JITTER = 0.1;

/**
 * The base poll interval in seconds: the manifest's `poll_interval_s` when it is a
 * positive number, else the built-in default. Stretched to at least
 * WS_CONNECTED_POLL_S while the live socket is connected (but a larger manifest
 * value is preserved — `max`, never a busier cadence than the server asked for).
 */
export function resolvePollIntervalS(manifestPollS: number | null, wsConnected: boolean): number {
  const base =
    manifestPollS !== null && manifestPollS > 0 ? manifestPollS : DEFAULT_POLL_INTERVAL_S;
  return wsConnected ? Math.max(base, WS_CONNECTED_POLL_S) : base;
}

/**
 * Device-stable jitter factor in [1-POLL_JITTER, 1+POLL_JITTER). Derived from a
 * seed in [0,1) — pass a per-device value (e.g. token hash mapped to [0,1)) so a
 * device keeps a consistent offset and the fleet spreads out.
 */
export function jitterFactor(seed: number): number {
  const clamped = seed < 0 ? 0 : seed >= 1 ? 0.999999 : seed;
  return 1 + (clamped * 2 - 1) * POLL_JITTER;
}

/** Apply the jitter factor to a base millisecond delay (rounded). */
export function applyJitter(baseMs: number, seed: number): number {
  return Math.round(baseMs * jitterFactor(seed));
}
